import { inject, Injectable, signal } from '@angular/core';
import { Subject } from "rxjs";
import postRobot from 'post-robot';
import { EnvironmentService } from './environment.service';
import { HostPageNavigatedPayload, HostAppContext, OnRoadPlatformExtension } from "./host-application.interfaces";


@Injectable({ providedIn: "root" })
export class HostApplicationService {
  #context = signal<HostAppContext | undefined>(undefined);
  #environmentService = inject(EnvironmentService);

  // events coming from host application
  public onThemeUpdated$: Subject<string> = new Subject<string>();
  public onAuthTokenUpdated$: Subject<string> = new Subject<string>();
  public onContextUpdated$: Subject<HostAppContext> = new Subject<HostAppContext>();
  public onHostPageNavigated$: Subject<HostPageNavigatedPayload> = new Subject<HostPageNavigatedPayload>();

  private secureChannel = {
    window: window.parent,
    domain: "*" // TODO: Parent domain can be set so that post-robot only communicates with trusted parent
  };

  constructor(
    private environmentService: EnvironmentService
  ) {
    this.initParentComms();
  }

  public get isEmbedded(): boolean {
    return window.self !== window.top;
  }

  private initParentComms() {

    postRobot.on("changeTheme", this.secureChannel, async (event) => {
      if (this.#environmentService.isDev()) {
        console.debug("changeTheme: ", event.data.theme);
      }
      this.onThemeUpdated$.next(event.data.theme);
    });

    postRobot.on("updateApiAuthToken", this.secureChannel, async (event) => {
      if (this.#environmentService.isDev()) {
        console.debug("updateApiAuthToken: ", event.data.token);
      }
      this.onAuthTokenUpdated$.next(event.data.token);
    });

    postRobot.on("updateContext", this.secureChannel, async (event) => {
      let hostContext = (event.data || {}) as HostAppContext;
      if (this.#environmentService.isDev()) {
        console.debug("updateContext: ", hostContext);
      }
      if (hostContext) {
        this.#context.set(hostContext);
        this.onContextUpdated$.next(hostContext);
      }
    });

    /*
      * Notify when host page navigation occurs (e.g., user navigates to a different route/iframe)
    */
    postRobot.on("hostPageNavigated", this.secureChannel, async (event) => {
      if (this.#environmentService.isDev()) {
        console.debug("hostPageNavigated: ", event);
      }
      this.onHostPageNavigated$.next(event.data as HostPageNavigatedPayload);
    });

  }

  public async getCurrentTheme(): Promise<string | null> {
    if (this.isEmbedded) {
      let res = await postRobot.send(this.secureChannel.window, "getTheme", {}, { domain: this.secureChannel.domain });
      return res.data as unknown as string ?? null;
    }
    return null;
  }

  public async getUserToken(): Promise<string | null> {
    if (!this.isEmbedded) {
      console.debug("HostApplicationService.getUserToken called in non-embedded mode");
      return null;
    }
    let res = await postRobot.send(this.secureChannel.window, "getUserToken", {}, { domain: this.secureChannel.domain });
    return res.data as unknown as string ?? null;
  }

  public get getCurrentContext() {
    return this.#context;
  }

  public async loadContext() {
    if (this.isEmbedded) {
      let res = await postRobot.send(this.secureChannel.window, "getContext", {}, { domain: this.secureChannel.domain });
      if (this.#environmentService.isDev()) {
        console.info("Context received from Host Application: ", res.data);
      }
      let hostContext = (res.data || {}) as HostAppContext;
      this.#context.set(hostContext);
    } else {
      this.#context.set(undefined);
    }
  }

}
